import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
	Button,
	Collapse,
	Label,
	Modal,
	ModalBody,
	ModalHeader,
	Tooltip,
	UncontrolledTooltip,
} from "reactstrap";
import moment from "moment";
import jsPDF from "jspdf";

import "../../styles/DataValidation.css";
import "../../styles/StackPlots.css";
import { default as utilIcons } from "../../resources/utilIcons";
import { colors } from "../../styles/colors";

import ValidationPlot from "./ValidationPlot";
import { data as dummyData, config as dummyConfig } from "./StackPlotDummy";
import { ModalContext, RestContext } from "../../App";
import { DATA_URL, STACKPLOT_URL, VALIDATION_URL } from "../../serverConfig";
import { checkMobile, isNumeric } from "../../util/globals";
import SearchableSelect from "../../components/SearchableSelect";
import SortControls from "../../components/SortControls";
import { deTzTimestamp } from "../../util/timeUtils";

const DataValidationContext = createContext(null);

const AQMET_COLUMNS = {
	recordDate: "Record Date",
	parCode: "Par Code",
	rawVal: "Raw Val",
	screenFlag: "Screening Flag",
	loggerFlag: "Logger Flag",
	qualifierCode: "Qualifier Code(s)",
	validationCode: "Validation Code",
	controlVal: "Control Val",
	validatedVal: "Validated Val",
	sourceCode: "Source Code",
	status: "Status",
	insertDate: "Insert Date",
	updateDate: "Update Date",
	updateBy: "Update By",
};

const EDITABLE_COLUMNS = {
	recordDate: false,
	parCode: false,
	rawVal: true,
	screenFlag: false,
	loggerFlag: false,
	qualifierCode: true,
	validationCode: false,
	controlVal: true,
	validatedVal: false,
	sourceCode: true,
	status: true,
	insertDate: false,
	updateDate: false,
	updateBy: false,
};

const COLUMN_INDECES = {
	recordDate: 1,
	parCode: 2,
	rawVal: 3,
	screenFlag: 4,
	loggerFlag: 5,
	qualifierCode: 6,
	validationCode: 7,
	controlVal: 8,
	validatedVal: 9,
	sourceCode: 10,
	status: 11,
	insertDate: 12,
	updateDate: 13,
	updateBy: 14,
};

function DataValidation({ selectedSite }) {
	const [dateRange, setDateRange] = useState({});
	const [parCodeList, setParCodeList] = useState([]);
	const [selectedPars, setSelectedPars] = useState([]);
	const [columns, setColumns] = useState([]);
	const [data, setData] = useState([]);
	const [stackplotData, setStackplotData] = useState({});
	const [loading, setLoading] = useState(false);
	const [loadingProgress, setLoadingProgress] = useState(1);
	const [chartRefs, setChartRefs] = useState();
	const [callbacks, setCallbacks] = useState({});
	const [dataChanged, setDataChanged] = useState(false);
	const [dataChanges, setDataChanges] = useState(new Map());
	const [optionsChanged, setOptionsChanged] = useState(false);
	const [highlighted, setHighlighted] = useState({});
	const [sortedData, setSortedData] = useState(null);
	const [dataToSortedMap, setDataToSortedMap] = useState({});
	const [sortedToDataMap, setSortedToDataMap] = useState({});
	const [stackPlotConfig, setStackPlotConfig] = useState(null);
	const [errorCode, setErrorCode] = useState(null); // Oracle error code returned by bad validation request

	const { sendGetRequest, sendPostRequest } = useContext(RestContext);
	const { openModal, setOpenModal } = useContext(ModalContext);

	const loadingOptions = useRef(loading);
	const stackPlotRef = useRef(null);

	const toggleFilterModal = () =>
		openModal === "validation-filter-modal"
			? setOpenModal()
			: setOpenModal("validation-filter-modal");

	useEffect(() => {
		setDateRange({
			min: moment()
				.set("hour", 0)
				.set("minute", 0)
				.set("second", 0)
				.set("millisecond", 0)
				.set("date", 1)
				.subtract(1, "month")
				.format("YYYY-MM-DDTHH:mm"),
			max: moment()
				.set("hour", 0)
				.set("minute", 0)
				.set("second", 0)
				.set("millisecond", 0)
				.set("date", 1)
				.format("YYYY-MM-DDTHH:mm"),
		});
		// setHighlighted((values) => ({...values, "2025-11-01T07:00:00.000+00:00": ["O3-4"]}));
	}, []);

	useEffect(() => {
		setOptionsChanged(selectedPars.length > 0); // Can load data whenever date range is updated or selected parameter list is updated (and not empty)
		resetDataChanges();
		setDataChanged(false);
		if (!!errorCode) {
			setErrorCode(null);
		}
	}, [dateRange, selectedPars]);

	useEffect(() => {
		setSortedData(data);
		const newIndexMap = Object.fromEntries(
			data.map((val, index) => [index, index]),
		);
		setDataToSortedMap(newIndexMap);
		setSortedToDataMap(newIndexMap);
		convertDataForStackPlots(data);
		generateConfig();
	}, [data]);

	useEffect(() => {
		resetTableChanges();
		applyDataChangesToTable();
	}, [dataToSortedMap]);

	const sortData = (sortFn) => {
		if (!sortFn) return;
		const newSortedData = [...data];
		newSortedData.sort(sortFn);
		const newDataToSortedMap = {};
		const newSortedToDataMap = {};
		for (let index in newSortedData) {
			const dataIndex = data.indexOf(newSortedData[index]);
			newDataToSortedMap[dataIndex] = index;
			newSortedToDataMap[index] = dataIndex;
		}
		setDataToSortedMap(newDataToSortedMap);
		setSortedToDataMap(newSortedToDataMap);
		setSortedData(newSortedData);
	};

	const convertDataForStackPlots = (data) => {
		if (!data?.length) return;
		const timestamps = [];
		const minTimestamp = moment(data[0].recordDate).unix();
		const maxTimestamp = moment(data[data.length - 1].recordDate).unix();
		for (let i = minTimestamp; i <= maxTimestamp; i += 3600) {
			timestamps.push(i);
		}
		const convertedData = { _timestamps: timestamps };
		for (let row of data) {
			if (!(row.parCode in convertedData)) {
				convertedData[row.parCode] = {};
			}
			const timestamp = moment(row.recordDate).unix();
			convertedData[row.parCode][timestamp] = [
				row.rawVal,
				row.loggerFlag ? row.loggerFlag : row.screenFlag,
			];
			if (!(row.parCode + "_VAL" in convertedData)) {
				convertedData[row.parCode + "_VAL"] = {};
			}
			convertedData[row.parCode + "_VAL"][timestamp] = [
				row.validatedVal,
				row.validationCode,
			];
		}
		setStackplotData(convertedData);
	};

	const beginLoad = () => {
		dataChanges.clear();
		const payload = {
			siteId: selectedSite,
			minDate: deTzTimestamp(dateRange.min),
			maxDate: deTzTimestamp(dateRange.max),
			parCodeList: selectedPars.map((par) => par.parCode),
		};
		setLoading(true);
		sendPostRequest(
			VALIDATION_URL + "/getData",
			payload,
			(response) => {
				setData(response.data);
				// setHighlighted(
				//     Object.fromEntries(
				//         response.data.map((row) => [row.recordDate, []])
				//     )
				// ); // Initialize highlighted with timestamps as keys and empty arrays as values
				setLoading(false);
				setOptionsChanged(false);
			},
			(error) => {
				setLoading(false);
			},
		);
	};

	const getMinDate = () => {
		if (!data?.length) return 0;
		return data[0].recordDate;
	};

	const getMaxDate = () => {
		if (!data?.length) return 0;
		return data[data.length - 1].recordDate;
	};

	const getMin = (data) => {
		if (data.length === 0) return -Infinity;
		else if (data.length === 1) return data[0];
		else {
			let min = data[0];
			for (let i = 1; i < data.length; i++) {
				if (data[i] < min) min = data[i];
			}
			return min;
		}
	};

	const getMax = (data) => {
		if (data.length === 0) return Infinity;
		else if (data.length === 1) return data[0];
		else {
			let max = data[0];
			for (let i = 1; i < data.length; i++) {
				if (data[i] > max) max = data[i];
			}
			return max;
		}
	};

	const doXScroll = (scrollOptions) => {
		if (
			!dateRange ||
			dateRange.min === undefined ||
			dateRange.max === undefined ||
			!stackplotData ||
			!stackplotData._timestamps ||
			stackplotData._timestamps.length < 2
		)
			return;
		const { direction, quantity, unit } = scrollOptions;
		let offset = 0;
		switch (direction) {
			case "left":
				let dataMin = moment.unix(stackplotData._timestamps[0]);
				let currentMin = moment(dateRange.min, "YYYY-MM-DDTHH:mm");
				let targetMin = moment(currentMin).subtract(quantity, unit);
				if (targetMin.unix() < dataMin.unix()) {
					offset = currentMin.unix() - dataMin.unix();
				} else {
					offset = currentMin.unix() - targetMin.unix();
				}
				setDateRange({
					min: currentMin
						.subtract(offset, "seconds")
						.format("YYYY-MM-DDTHH:mm"),
					max: moment(dateRange.max, "YYYY-MM-DDTHH:mm")
						.subtract(offset, "seconds")
						.format("YYYY-MM-DDTHH:mm"),
				});
				return;
			case "right":
				let dataMax = moment.unix(
					stackplotData._timestamps[
						stackplotData._timestamps.length - 1
					],
				);
				let currentMax = moment(dateRange.max, "YYYY-MM-DDTHH:mm");
				let targetMax = moment(currentMax).add(quantity, unit);
				if (targetMax.unix() > dataMax.unix()) {
					offset = dataMax.unix() - currentMax.unix();
				} else {
					offset = targetMax.unix() - currentMax.unix();
				}
				setDateRange({
					min: moment(dateRange.min, "YYYY-MM-DDTHH:mm")
						.add(offset, "seconds")
						.format("YYYY-MM-DDTHH:mm"),
					max: currentMax
						.add(offset, "seconds")
						.format("YYYY-MM-DDTHH:mm"),
				});
				return;
			default:
				return;
		}
	};

	const updateTimeframe = (minTimestamp, maxTimestamp) => {
		setDateRange({
			min: minTimestamp,
			max: maxTimestamp,
		});
	};

	const handleMinDate = (event) => {
		const value = event.target.value;
		setDateRange((values) => ({ ...values, min: value }));
	};

	const handleMaxDate = (event) => {
		const value = event.target.value;
		setDateRange((values) => ({ ...values, max: value }));
	};

	const generateConfig = () => {
		const newConfig = {
			fullTitle: null,
			footnote: null,
			chartSpacing: 20,
			charts: [],
		};
		if (!selectedPars?.length) {
			return newConfig;
		} else {
			for (let par of selectedPars) {
				const parConfig = {
					parCode: par.parCode,
					unit: par.unitCode,
					type: "line",
					side: "L",
					color: "0000FF",
					symbol: null,
					rawPrec: par.rawPrec,
				};
				const chartConfig = {
					leftMin: null,
					leftMax: null,
					rightMin: null,
					rightMax: null,
					chartHeight: 100,
					hasRightSide: false,
					parameters: [parConfig],
				};
				newConfig.charts.push(chartConfig);
			}
		}
		setStackPlotConfig(newConfig);
	};

	const registerCallback = (callbackName, callbackFn) => {
		setCallbacks((values) => ({ ...values, [callbackName]: callbackFn }));
	};

	const scrollTableOnCursor = (timestamp, parCode, index, chartIndex) => {
		console.log(timestamp, parCode, index, chartIndex);
		const ts = moment(timestamp).utc().format("YYYY-MM-DDTHH:mm:ss.SSSZ");
		console.log(ts);
		let l = 0,
			r = data.length,
			m = Math.trunc((l + r) / 2);
		let row;
		while (l < m) {
			row = data[m];
			if (row.recordDate === ts) {
				break;
			} else if (row.recordDate < ts) {
				l = m;
			} else {
				r = m;
			}
			m = Math.trunc((l + r) / 2);
		}
		if (!(row.parCode === parCode && row.recordDate === ts)) {
			l = m - 1;
			r = m + 1;
			let lRow = data[l],
				rRow = data[r];
			while (lRow.recordDate === ts || rRow.recordDate === ts) {
				if (lRow.recordDate === ts && lRow.parCode === parCode) {
					m = l;
					break;
				} else if (rRow.recordDate === ts && rRow.parCode === parCode) {
					m = r;
					break;
				}
				l--;
				r++;
				lRow = data[l];
				rRow = data[r];
			}
		}
		const tableIndex = dataToSortedMap[m];
		const el = document.querySelector(
			`#validation-table .validation-table-row:nth-child(${tableIndex})`,
		);
		el.scrollIntoView();
	};

	const resetDataChanges = () => {
		resetTableChanges();
		dataChanges.clear();
		setDataChanged(false);
	};

	const resetTableChanges = () => {
		const inputs = document.querySelectorAll(
			`#validation-table input.validation-table-input.changed`,
		);
		for (let input of inputs) {
			input.value = null;
			input.classList.remove("changed");
		}
		const readOnlyCells = document.querySelectorAll(
			"#validation-table td.validation-table-cell-readonly.changed",
		);
		for (let cell of readOnlyCells) {
			cell.classList.remove("changed");
			cell.innerHTML = "";
		}
	};

	const applyValidationInfo = (payload) => {
		if (!highlighted) return;
		const { selectedPars, validationCode, qualifierCode, controlVal } =
			payload;
		const highlightedTimestamps = Object.keys(highlighted);
		const minTimestamp = getMin(highlightedTimestamps),
			maxTimestamp = getMax(highlightedTimestamps);
		for (let index in data) {
			const row = data[index];
			if (row.recordDate < minTimestamp) {
				continue;
			} else if (row.recordDate > maxTimestamp) {
				break;
			} else {
				if (
					highlighted[row.recordDate]?.includes(row.parCode) &&
					selectedPars[row.parCode]
				) {
					const newRow = dataChanges.get(index) || { ...row };
					newRow.validationCode = validationCode;
					newRow.qualifierCode = qualifierCode;
					newRow.controlVal = controlVal;
					dataChanges.set(index, newRow);
				}
			}
		}
		applyDataChangesToTable();
		setDataChanged(true);
	};

	const applyDataChangesToTable = () => {
		if (!dataToSortedMap) return;
		for (let index of dataChanges.keys()) {
			const adjIndex = dataToSortedMap[index];
			const rowEl = document.querySelector(
				`#validation-table tbody>tr:nth-child(${Number(adjIndex) + 1})`,
			);
			const dataRow = data[index];
			const changedRow = dataChanges.get(index);
			for (let column of [
				"rawVal",
				"qualifierCode",
				"validationCode",
				"controlVal",
				"sourceCode",
				"status",
			]) {
				if (dataRow[column] !== changedRow[column]) {
					const cellEl = rowEl.querySelector(
						`td:nth-child(${COLUMN_INDECES[column] + 1})`,
					);
					if (
						cellEl.classList.contains(
							"validation-table-cell-readonly",
						)
					) {
						cellEl.classList.add("changed");
						cellEl.innerHTML = changedRow[column];
					} else {
						const cellInput = cellEl.querySelector("input");
						cellInput.value = changedRow[column];
						cellInput.classList.add("changed");
					}
				}
			}
		}
	};

	const saveChanges = () => {
		if (!dataChanged) return;

		if (!!errorCode) {
			setErrorCode(null);
		}

		const payload = {
			siteId: selectedSite,
			data: [],
		};
		dataChanges.forEach((value) => {
			payload.data.push(value);
		});
		sendPostRequest(
			VALIDATION_URL + "/validate",
			payload,
			(response) => {
				const newData = [...data];

				dataChanges.forEach((value, key) => {
					newData[key] = value;
				});
				resetDataChanges();
				setData(newData);
			},
			(error) => {
				const code = error.response?.data?.errorCode || null;
				setErrorCode(code);
			},
		);
	};

	const autoValidate = () => {
		const payload = {
			siteId: selectedSite,
			startDate: moment(dateRange.min).toISOString(),
			endDate: moment(dateRange.max).toISOString(),
		};
		console.log(payload);
		sendPostRequest(VALIDATION_URL + "/auto", payload, (response) => {
			beginLoad();
		});
	};

	return (
		<DataValidationContext.Provider
			value={{
				dateRange,
				updateTimeframe,
				parCodeList,
				setParCodeList,
				setSelectedPars,
				handleMinDate,
				handleMaxDate,
				getMinDate,
				getMaxDate,
				doXScroll,
				loading,
				dataChanges,
				setDataChanges,
				dataChanged,
				setDataChanged,
				optionsChanged,
				setOptionsChanged,
				saveChanges,
				autoValidate,
				highlighted,
				setHighlighted,
				dataToSortedMap,
				sortedToDataMap,
			}}
		>
			<div id="data-validation">
				<ValidationControls
					selectedSite={selectedSite}
					beginLoad={beginLoad}
					toggleFilterModal={toggleFilterModal}
					errorCode={errorCode}
					setErrorCode={setErrorCode}
				/>
				<div id="validation-content-wrapper">
					<div id="stackplot-controls-help">
						<div className="stackplot-controls-help-blurb">
							<b>Drag:</b> Pan X Axis (all charts)
						</div>
						<div className="stackplot-controls-help-blurb">
							<b>Ctrl+Scroll:</b> Zoom X Axis (all charts)
						</div>
						<div className="stackplot-controls-help-blurb">
							<b>Shift+Drag</b> Pan Y Axis (single chart)
						</div>
						<div className="stackplot-controls-help-blurb">
							<b>Shift+Scroll:</b> Zoom Y Axis (single chart)
						</div>
						<div className="stackplot-controls-help-blurb">
							<b>Shift+Dbl Click:</b> Reset Y Axis (single chart)
						</div>
					</div>
					<ValidationPlot
						data={stackplotData}
						config={stackPlotConfig}
						dateRange={dateRange}
						stackPlotRef={stackPlotRef}
						bubbleChartRefs={setChartRefs}
						callbacks={callbacks}
						highlighted={highlighted}
						setHighlighted={setHighlighted}
						scrollTableOnCursor={scrollTableOnCursor}
					/>
					<ValidationTable
						data={sortedData ? sortedData : data}
						updateData={setData}
						registerCallback={registerCallback}
						highlighted={highlighted}
						setHighlighted={setHighlighted}
						callbacks={callbacks}
					/>
				</div>

				<LoadingScreen
					hidden={!loading}
					loadingProgress={loadingProgress}
					isMobile={checkMobile()}
				/>
				<DataFilterModal
					isOpen={openModal === "validation-filter-modal"}
					toggle={toggleFilterModal}
					sortData={sortData}
				/>
				<ValidationPopover
					selectedSite={selectedSite}
					registerCallback={registerCallback}
					highlighted={highlighted}
					applyValidationInfo={applyValidationInfo}
				/>
			</div>
		</DataValidationContext.Provider>
	);
}

function ValidationControls({
	selectedSite,
	beginLoad,
	toggleFilterModal,
	errorCode,
}) {
	const [errorTooltip, setErrorTooltip] = useState(false);
	const {
		dateRange,
		updateTimeframe,
		handleMinDate,
		handleMaxDate,
		getMinDate,
		getMaxDate,
		selectedOptions,
		doXScroll,
		loading,
		dataChanged,
		optionsChanged,
		saveChanges,
		autoValidate,
	} = useContext(DataValidationContext);

	const toggleErrorTooltip = () => {
		if (!!errorCode) {
			setErrorTooltip(!errorTooltip);
		} else {
			setErrorTooltip(false);
		}
	};

	const getErrorMessage = () => {
		switch (errorCode) {
			case 20001:
				return "Validation Codes not starting with 'V' must have Validated Val of -999";
			case 20002:
				return "Validated Val of -999 must not have Validation Code starting with 'V'";
			case 20003:
				return "Raw Val and Validated Val should match for Validation Code 'V'";
			case 20004:
				return "Control Val must not be empty for Validation Code 'VA'";
			case 20005:
				return "Data is locked and cannot be updated.";
			default:
				return "Oracle Error";
		}
	};

	const datesInSameMonth = () => {
		if (!dateRange?.min || !dateRange?.max) return false;
		const start = moment(dateRange.min),
			end = moment(dateRange.max);

		return (
			start.year() === end.year() &&
			start.month() === end.month() &&
			start < end
		);
	};

	return (
		<div id="validation-controls">
			<div id="validation-controls-left">
				<div id="validation-controls-left-top">
					<div className="stackplot-control-wrapper">
						<Label>Timeframe:</Label>
						<StackPlotTimeFrameSelector
							updateTimeframe={updateTimeframe}
						/>
					</div>
					<ParCodeSelector selectedSite={selectedSite} />
					<Button
						id="validation-load-button"
						onClick={beginLoad}
						disabled={!optionsChanged}
					>
						Load Data
					</Button>
				</div>
				<div id="validation-controls-left-bottom">
					<Button
						id="validation-filter-modal-toggle"
						onClick={toggleFilterModal}
					>
						Sort
					</Button>
					<Button
						id="auto-validate-button"
						onClick={autoValidate}
						disabled={!datesInSameMonth() || optionsChanged}
					>
						Auto Validate
					</Button>
					<Button
						id="validation-save-button"
						className={!!errorCode ? "error" : ""}
						onClick={saveChanges}
						disabled={!dataChanged}
					>
						Save Changes
					</Button>
					<Tooltip
						isOpen={errorTooltip}
						target="validation-save-button"
						placement="bottom"
						toggle={toggleErrorTooltip}
					>
						{getErrorMessage(errorCode)}
					</Tooltip>
				</div>
			</div>

			<div id="x-zoom-controls" className="stackplot-control-wrapper">
				<Label>View:</Label>
				<input
					type="datetime-local"
					onChange={handleMinDate}
					min={getMinDate()}
					max={dateRange.max || getMaxDate()}
					step={
						selectedOptions
							? selectedOptions.precision.match(/\d+-min/)
								? 60
								: 3600
							: 1
					}
					value={dateRange.min || ""}
				/>
				<span>-</span>
				<input
					type="datetime-local"
					onChange={handleMaxDate}
					min={dateRange.min || getMinDate()}
					max={getMaxDate()}
					step={
						selectedOptions
							? selectedOptions.precision.match(/\d+-min/)
								? 60
								: 3600
							: 1
					}
					value={dateRange.max || ""}
				/>
				<DateScroller doXScroll={doXScroll} />
			</div>
			<Button
				id="stackplots-refresh-button"
				onClick={beginLoad}
				disabled={loading}
			>
				{utilIcons.refresh(colors["ars-neutral-900"])}
			</Button>
		</div>
	);
}

function StackPlotTimeFrameSelector({ updateTimeframe }) {
	const [dateRange, setDateRange] = useState({ min: null, max: null });

	useEffect(() => {
		init();
	}, []);

	const init = () => {
		setDateRange({
			min: moment()
				.set("hour", 0)
				.set("minute", 0)
				.set("second", 0)
				.set("millisecond", 0)
				.set("date", 1)
				.subtract(1, "month")
				.format("YYYY-MM-DDTHH:mm"),
			max: moment()
				.set("hour", 0)
				.set("minute", 0)
				.set("second", 0)
				.set("millisecond", 0)
				.set("date", 1)
				.subtract(1, "minute")
				.format("YYYY-MM-DDTHH:mm"),
		});
	};

	const fireUpdate = (event) => {
		const allowedIds = [
			"stackplot-timeframe-numdays-input",
			"stackplot-timeframe-mindate-input",
			"stackplot-timeframe-maxdate-input",
			"stackplot-timeframe-mode-select",
		];
		if (
			event.relatedTarget?.id &&
			allowedIds.includes(event.relatedTarget.id)
		)
			return;
		if (dateRange.min && dateRange.max) {
			updateTimeframe(dateRange.min, dateRange.max);
		}
	};

	const handleMinDate = (event) => {
		const value = event.target.value;
		setDateRange((values) => ({ ...values, min: value }));
	};

	const handleMaxDate = (event) => {
		const value = event.target.value;
		setDateRange((values) => ({ ...values, max: value }));
	};

	return (
		<div id="stackplot-timeframe-selector">
			<input
				id="stackplot-timeframe-mindate-input"
				type="datetime-local"
				value={dateRange.min}
				onChange={handleMinDate}
				step={3600}
				onBlur={fireUpdate}
			/>
			<span>to</span>
			<input
				id="stackplot-timeframe-maxdate-input"
				type="datetime-local"
				value={dateRange.max}
				onChange={handleMaxDate}
				onBlur={fireUpdate}
			/>
		</div>
	);
}

function DateScroller({ doXScroll }) {
	const [quantity, setQuantity] = useState(1);
	const [unit, setUnit] = useState("days");

	const handleQuantity = (event) => {
		const value = Number(event.target.value);
		setQuantity(value);
	};

	const handleUnit = (event) => {
		const value = event.target.value;
		setUnit(value);
	};

	const handleMinus = () => {
		doXScroll({
			direction: "left",
			quantity,
			unit,
		});
	};

	const handlePlus = () => {
		doXScroll({
			direction: "right",
			quantity,
			unit,
		});
	};

	return (
		<div className="date-scroller">
			<Button onClick={handleMinus}>-</Button>
			<input
				className="time-quantity-input"
				type="number"
				step={1}
				value={quantity}
				onChange={handleQuantity}
			/>
			<select
				className="time-unit-select"
				value={unit}
				onChange={handleUnit}
			>
				<option value="hours">Hour(s)</option>
				<option value="days">Day(s)</option>
				<option value="weeks">Week(s)</option>
				<option value="months">Month(s)</option>
			</select>
			<Button onClick={handlePlus}>+</Button>
		</div>
	);
}

function ParCodeSelector({ selectedSite }) {
	const [selectedParCodeList, setSelectedParCodeList] = useState([]);
	const [allParCodes, setAllParCodes] = useState(false);
	const [isOpen, setIsOpen] = useState(false);

	const ref = useRef(null);

	const { sendGetRequest, sendPostRequest } = useContext(RestContext);

	const { dateRange, parCodeList, setParCodeList, setSelectedPars } =
		useContext(DataValidationContext);

	const toggleOpen = () => setIsOpen(!isOpen);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (ref.current && isOpen) {
				if (
					!ref.current.contains(event.target) &&
					event.target.id !== "site-select"
				) {
					toggleOpen();
				}
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [ref, isOpen, toggleOpen]);

	useEffect(() => {
		if (allParCodes) {
			sendGetRequest(VALIDATION_URL + "/parameters/all", (response) => {
				setParCodeList(response.data);
			});
		} else {
			if (dateRange?.min && dateRange?.max) {
				const payload = {
					siteId: selectedSite,
					minDate: deTzTimestamp(dateRange.min),
					maxDate: deTzTimestamp(dateRange.max),
				};
				sendPostRequest(
					VALIDATION_URL + "/parameters",
					payload,
					(response) => {
						setParCodeList(response.data);
					},
				);
			}
		}
	}, [selectedSite, allParCodes, dateRange]);

	useEffect(() => {
		const newSelected = [];
		for (let par of selectedParCodeList) {
			const foundPar = parCodeList.find(
				(other) => other.parCode === par.parCode,
			);
			if (foundPar) newSelected.push(foundPar);
		}
		newSelected.sort(compPars);
		setSelectedParCodeList(newSelected);
	}, [parCodeList]);

	useEffect(() => {
		setSelectedPars(selectedParCodeList);
	}, [selectedParCodeList]);

	const compPars = (a, b) => {
		return parCodeList.indexOf(a) - parCodeList.indexOf(b);
	};

	const togglePar = (par) => {
		const newSelected = [...selectedParCodeList];
		const foundPar = selectedParCodeList.find(
			(other) => other.parCode === par.parCode,
		);
		if (foundPar) {
			newSelected.splice(newSelected.indexOf(foundPar), 1);
		} else {
			newSelected.push(par);
		}
		newSelected.sort(compPars);
		setSelectedParCodeList(newSelected);
	};

	const getSelectedList = () => {
		if (!selectedParCodeList.length) {
			return <span className="no-pars-selected">Select Parameters</span>;
		} else {
			return selectedParCodeList.map((par) => par.parCode).join(", ");

			// return `${selectedParCodeList.length} selected`;
		}
	};

	const isSelected = (par) => {
		return selectedParCodeList.some(
			(other) => other.parCode === par.parCode,
		);
	};

	const reorder = (index, newIndex) => {
		if (
			index < 0 ||
			index >= parCodeList.length ||
			newIndex < 0 ||
			newIndex >= parCodeList.length
		)
			return;
		const newList = [...parCodeList];
		newList[index] = parCodeList[newIndex];
		newList[newIndex] = parCodeList[index];
		setParCodeList(newList);
	};

	const mapParCodesToList = () => {
		return parCodeList.map((par, index) => (
			<ParCodeListEntry
				par={par}
				parIndex={index}
				isSelected={isSelected(par)}
				togglePar={togglePar}
				reorder={reorder}
			/>
		));
	};

	return (
		<div
			id="validation-par-code-selector-wrapper"
			className="stackplot-control-wrapper"
			ref={ref}
		>
			<div id="validation-par-code-selector">
				<div id="par-code-selector-display" onClick={toggleOpen}>
					{getSelectedList()}
				</div>
				<Collapse id="par-code-selector-collapse" isOpen={isOpen}>
					<div id="par-code-selector-list-container">
						{mapParCodesToList()}
					</div>
				</Collapse>
			</div>
		</div>
	);
}

function ParCodeListEntry({ par, parIndex, isSelected, togglePar, reorder }) {
	const getAbbr = () => {
		if (!par?.parAbbr) return "";
		return par.parAbbr;
	};

	const handleClick = () => {
		togglePar(par);
	};

	return (
		<div className="validation-par-code-list-entry" onClick={handleClick}>
			<SortControls size="small" index={parIndex} updateFn={reorder} />
			<input type="checkbox" checked={isSelected} />
			<div className="validation-par-code-list-entry-abbr">
				{getAbbr()}
			</div>
		</div>
	);
}

function ValidationTable({ data, highlighted, setHighlighted, callbacks }) {
	const [size, setSize] = useState("half");

	const anchorIndex = useRef(null);
	const lastClickedIndex = useRef(null);

	const { dataChanges, setDataChanged, sortedToDataMap, dataToSortedMap } =
		useContext(DataValidationContext);

	const getHeaders = () => {
		if (!data.length) return null;
		return (
			<tr>
				<th className="validation-table-row-select">
					<input
						type="checkbox"
						checked={anyHighlighted()}
						onClick={handleHeaderCheckboxClick}
					/>
				</th>
				{Object.keys(AQMET_COLUMNS).map((column) => (
					<th>{AQMET_COLUMNS[column]}</th>
				))}
			</tr>
		);
	};

	const getRows = () => {
		if (!data.length) return null;
		const columns = Object.keys(AQMET_COLUMNS);
		return data.map((row, index) => (
			<tr
				className={
					"validation-table-row" +
					(highlighted[row.recordDate]?.includes(row.parCode)
						? " highlighted"
						: "")
				}
				onContextMenu={callbacks.openPopover}
			>
				<td
					className="validation-table-row-select"
					onMouseDown={(e) => handleCheckboxClick(e, row, index)}
				>
					<input
						type="checkbox"
						checked={
							highlighted[row.recordDate]?.includes(
								row.parCode,
							) || false
						}
					/>
				</td>
				{columns.map((column) => {
					if (EDITABLE_COLUMNS[column]) {
						return (
							<td
								className="validation-table-cell-editable"
								onClick={handleInputCellClick}
							>
								<input
									type="text"
									className="validation-table-input"
									placeholder={row[column]}
									onChange={(event) => {
										handleCellInput(
											event,
											index,
											row,
											column,
										);
									}}
								/>
							</td>
						);
					} else {
						return (
							<td className="validation-table-cell-readonly">
								{formatColumn(column, row[column])}
							</td>
						);
					}
				})}
			</tr>
		));
	};

	const anyHighlighted = () => {
		return Object.keys(highlighted).length > 0;
	};

	const handleHeaderCheckboxClick = (event) => {
		const newHighlighted = {};
		if (!anyHighlighted()) {
			for (let row of data) {
				newHighlighted[row.recordDate] =
					newHighlighted[row.recordDate] || [];
				newHighlighted[row.recordDate].push(row.parCode);
			}
		}
		setHighlighted(newHighlighted);
	};

	const handleCheckboxClick = (event, row, index) => {
		event.preventDefault();
		event.stopPropagation();
		if (event.ctrlKey && event.shiftKey) {
			return;
		} else if (event.ctrlKey) {
			// Select multiple individual cells
			handleMultipleRowSelect(event, row, index);
		} else if (event.shiftKey) {
			// Select range of rows
			handleRangeRowSelect(event, row, index);
		} else {
			// Select single row
			handleSingleRowSelect(event, row, index);
		}
	};

	const handleSingleRowSelect = (event, row, index) => {
		let newPars = highlighted[row.recordDate] || [];
		let newHighlighted;
		if (newPars.includes(row.parCode)) {
			newHighlighted = { ...highlighted };
			newPars.splice(newPars.indexOf(row.parCode), 1);
		} else {
			newHighlighted = {};
			newPars.push(row.parCode);
		}
		if (newPars.length) {
			newHighlighted[row.recordDate] = newPars;
		} else {
			delete newHighlighted[row.recordDate];
		}
		setHighlighted(newHighlighted);
		lastClickedIndex.current = index;
		anchorIndex.current = index;
	};

	const handleMultipleRowSelect = (event, row, index) => {
		let newPars = highlighted[row.recordDate] || [];
		let newHighlighted = { ...highlighted };
		if (newPars.includes(row.parCode)) {
			newPars.splice(newPars.indexOf(row.parCode), 1);
		} else {
			newPars.push(row.parCode);
		}
		if (newPars.length) {
			newHighlighted[row.recordDate] = newPars;
		} else {
			delete newHighlighted[row.recordDate];
		}
		setHighlighted(newHighlighted);
		lastClickedIndex.current = index;
		anchorIndex.current = index;
	};

	const handleRangeRowSelect = (event, row, index) => {
		let newHighlighted = {};
		const range = data.slice(
			Math.min(index, anchorIndex.current),
			Math.max(index, anchorIndex.current) + 1,
		);
		if (index === lastClickedIndex.current) {
			let clear = true;
			for (let row of range) {
				if (!highlighted[row.recordDate]?.includes(row.parCode)) {
					clear = false;
					break;
				}
			}
			if (!clear) {
				for (let row of range) {
					newHighlighted[row.recordDate] =
						newHighlighted[row.recordDate] || [];
					newHighlighted[row.recordDate].push(row.parCode);
				}
			}
		} else {
			for (let row of range) {
				newHighlighted[row.recordDate] =
					newHighlighted[row.recordDate] || [];
				newHighlighted[row.recordDate].push(row.parCode);
			}
		}
		setHighlighted(newHighlighted);
		lastClickedIndex.current = index;
	};

	const formatColumn = (column, value) => {
		const formatFn = {
			recordDate: formatTimestamp,
			updateDate: formatTimestamp,
			insertDate: formatTimestamp,
		};
		if (column in formatFn) {
			return formatFn[column](value);
		} else {
			return value;
		}
	};

	const formatTimestamp = (timestamp) => {
		return moment(timestamp).format("MM/DD/YYYY HH:mm");
	};

	const handleInputCellClick = (event) => {
		if (event.target.tagName !== "INPUT") {
			const el = event.target.querySelector("input");
			el.focus();
		}
	};

	const handleCellInput = (event, index, row, column) => {
		if (!document.activeElement === event.target) return;
		const value = event.target.value;
		const el = event.target;
		el.classList.add("changed");
		const adjIndex = sortedToDataMap[index];
		dataChanges.set(adjIndex, { ...row, [column]: value });
		setDataChanged(true);
	};

	return (
		<div id="validation-table-wrapper" className={size}>
			<div id="validation-table-controls">
				<span>Table View</span>
				<Button
					onClick={() => {
						setSize("minimized");
					}}
				>
					{utilIcons.minimized(colors["ars-neutral-900"])}
				</Button>
				<Button
					onClick={() => {
						setSize("half");
					}}
				>
					{utilIcons.halfScreen(colors["ars-neutral-900"])}
				</Button>
				<Button
					onClick={() => {
						setSize("full");
					}}
				>
					{utilIcons.fullScreen(colors["ars-neutral-900"])}
				</Button>
			</div>
			<div id="validation-table">
				<table>
					<thead>{getHeaders()}</thead>
					<tbody>{getRows()}</tbody>
				</table>
			</div>
		</div>
	);
}

function LoadingScreen({ hidden, loadingProgress = 0, isMobile = false }) {
	const getContent = () => {
		if (isMobile) {
			return (
				<div className="loading-wrapper">
					<progress value={loadingProgress} />
					<span>{Math.round(loadingProgress * 1000) / 10}%</span>
				</div>
			);
		}
	};

	return (
		<div
			className={"loading" + (isMobile ? " mobile" : "")}
			hidden={hidden}
		>
			{getContent()}
		</div>
	);
}

function DataFilterModal({ isOpen, toggle, sortData }) {
	const [sortColumns, setSortColumns] = useState([
		["recordDate", "Asc"],
		["parCode", "Asc"],
	]);

	useEffect(() => {
		const sortFn = buildSortFunction(0);
		sortData(sortFn);
	}, [sortColumns]);

	const buildSortFunction = (index) => {
		if (index >= sortColumns.length) {
			return (a, b) => 0;
		}
		const column = sortColumns[index][0];
		const direction = sortColumns[index][1] === "Asc" ? 1 : -1;
		const nextColumnFn = buildSortFunction(index + 1);
		const sortFn = (a, b) => {
			if (!a || !b) return 0;
			else {
				if (a[column] === b[column]) {
					return nextColumnFn(a, b);
				} else {
					if (b[column] === null) return direction;
					else if (a[column] === null) return -direction;
					else return a[column] > b[column] ? direction : -direction;
				}
			}
		};
		return sortFn;
	};

	const getUnselectedColumns = () => {
		const unselectedColumns = Object.keys(AQMET_COLUMNS).filter(
			(column) =>
				!sortColumns.some((sortColumn) => sortColumn[0] === column),
		);
		return unselectedColumns;
	};

	const mapSortColumnsToOption = () => {
		return sortColumns.map((sortColumn, index) => (
			<DataSortOption
				index={index}
				isLast={index === sortColumns.length - 1}
				sortColumns={sortColumns}
				setSortColumns={setSortColumns}
				createOption={createOption}
				deleteOption={deleteOption}
			/>
		));
	};

	const createOption = () => {
		const newSortColumns = [
			...sortColumns,
			[getUnselectedColumns()[0], "Asc"],
		];
		setSortColumns(newSortColumns);
	};

	const deleteOption = (index) => {
		const newSortColumns = [...sortColumns];
		newSortColumns.splice(index, 1);
		setSortColumns(newSortColumns);
	};

	const resetSortColumns = () => {
		setSortColumns([
			["recordDate", "Asc"],
			["parCode", "Asc"],
		]);
	};

	return (
		<Modal id="validation-filter-modal" isOpen={isOpen} toggle={toggle}>
			<ModalHeader toggle={toggle}>Sort/Filter Data</ModalHeader>
			<ModalBody>
				<div id="validation-sort-options-wrapper">
					<h4>Sort by:</h4>
					<div id="validation-sort-options-container">
						{mapSortColumnsToOption()}
					</div>
					<Button
						id="validation-sort-reset"
						onClick={resetSortColumns}
					>
						Reset Sort
					</Button>
				</div>
			</ModalBody>
		</Modal>
	);
}

function DataSortOption({
	index,
	isLast,
	sortColumns,
	setSortColumns,
	createOption,
	deleteOption,
}) {
	const getOptionValue = () => {
		const option = sortColumns[index];
		return option[0];
	};

	const getOptionDirection = () => {
		const option = sortColumns[index];
		return option[1];
	};

	const toggleDirection = () => {
		const newOption = [...sortColumns[index]];
		if (getOptionDirection() === "Asc") {
			newOption[1] = "Desc";
		} else {
			newOption[1] = "Asc";
		}
		const newSortColumns = [...sortColumns];
		newSortColumns[index] = newOption;
		setSortColumns(newSortColumns);
	};

	const getSuffix = () => {
		if (isLast) {
			return (
				<Button
					className="validation-sort-option-direction-toggle"
					onClick={createOption}
				>
					+
				</Button>
			);
		} else {
			return <span>then</span>;
		}
	};

	const mapUnselectedColumnsToOption = () => {
		return Object.keys(AQMET_COLUMNS)
			.filter(
				(column) =>
					getOptionValue() === column ||
					!sortColumns.some((sortColumn) => sortColumn[0] === column),
			)
			.map((column) => (
				<option value={column}>{AQMET_COLUMNS[column]}</option>
			));
	};

	const handleSelect = (event) => {
		const value = event.target.value;
		const newOption = [value, "Asc"];
		const newSortColumns = [...sortColumns];
		newSortColumns[index] = newOption;
		setSortColumns(newSortColumns);
	};

	return (
		<div className="validation-sort-option">
			<Button
				className="validation-sort-option-delete"
				disabled={sortColumns.length === 1}
				onClick={() => deleteOption(index)}
			>
				-
			</Button>
			<select value={getOptionValue()} onChange={handleSelect}>
				{mapUnselectedColumnsToOption()}
			</select>
			<Button
				className="validation-sort-option-direction"
				onClick={toggleDirection}
			>
				{getOptionDirection()}
			</Button>
			{getSuffix()}
		</div>
	);
}

function ValidationPopover({
	selectedSite,
	registerCallback,
	highlighted,
	applyValidationInfo,
}) {
	const [valCodeList, setValCodeList] = useState([]);
	const [selectedPars, setSelectedPars] = useState({});
	const [validationCode, setValidationCode] = useState("V");
	const [qualifierCode, setQualifierCode] = useState(null);
	const [controlVal, setControlVal] = useState(null);
	const [pos, setPos] = useState(null);
	const [visible, setVisible] = useState(false);

	const ref = useRef(null);

	const { sendGetRequest } = useContext(RestContext);
	const { parCodeList } = useContext(DataValidationContext);

	const POPOVER_WIDTH = 400;
	const POPOVER_HEIGHT = 250;

	useEffect(() => {
		registerCallback("openPopover", handleOpenEvent);
	}, []);

	useEffect(() => {
		sendGetRequest(
			VALIDATION_URL + "/valCodes/" + selectedSite,
			(response) => {
				const responseData = response.data;
				responseData.sort((a, b) =>
					a.validationCode > b.validationCode ? 1 : -1,
				);
				setValCodeList(response.data);
			},
		);
	}, [selectedSite]);

	useEffect(() => {
		setSelectedPars(
			Object.fromEntries(
				getHighlightedPars().map((par) => [par.parCode, true]),
			),
		);
		setValidationCode("V");
		setQualifierCode(null);
		setControlVal(null);
	}, [highlighted]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (ref.current && pos) {
				if (!ref.current.contains(event.target)) {
					setPos(null);
				}
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [ref, pos]);

	useEffect(() => {
		setVisible(!!highlighted && Object.keys(highlighted).length);
	}, [highlighted]);

	const handleOpenEvent = (event) => {
		if (!event.shiftKey) {
			event.preventDefault();
			setPos({
				x: event.clientX - POPOVER_WIDTH / 2,
				y: event.clientY - POPOVER_HEIGHT / 2,
			});
		}
	};

	const getHighlightedPars = () => {
		if (!parCodeList || !highlighted) return [];
		const highlightedParCodes = [];
		Object.keys(highlighted).forEach((timestamp) => {
			for (let parCode of highlighted[timestamp]) {
				if (!highlightedParCodes.includes(parCode)) {
					highlightedParCodes.push(parCode);
				}
			}
		});
		const highlightedPars = [];
		highlightedParCodes.forEach((parCode) => {
			const par = parCodeList.find(
				(foundPar) => foundPar.parCode === parCode,
			);
			if (par) {
				highlightedPars.push(par);
			}
		});
		return highlightedPars;
	};

	const getXPos = () => {
		const x = pos?.x || 0;
		return x;
	};

	const getYPos = () => {
		return pos?.y || 0;
	};

	const isChecked = (parCode) => {
		if (!selectedPars) return false;
		return selectedPars[parCode];
	};

	const toggleParCode = (parCode) => {
		setSelectedPars((values) => ({
			...values,
			[parCode]: !selectedPars[parCode],
		}));
	};

	const mapParCodesToList = () => {
		return getHighlightedPars().map((par) => (
			<div
				className="validation-popover-parcode"
				onClick={() => toggleParCode(par.parCode)}
			>
				<input type="checkbox" checked={isChecked(par.parCode)} />
				<span>{par.parAbbr}</span>
			</div>
		));
	};

	const mapValCodesToList = () => {
		if (!valCodeList) return null;
		return valCodeList.map((valCode) => (
			<option
				value={valCode.validationCode}
			>{`${valCode.validationCode} - ${valCode.reason}`}</option>
		));
	};

	const isAdjusted = () => {
		return validationCode === "VA";
	};

	const handleTextInput = (event) => {
		const name = event.target.name;
		const value = event.target.value;
		if (name === "validationCode") {
			setValidationCode(value);
		} else if (name === "qualifierCode") {
			setQualifierCode(value);
		} else if (name === "controlVal") {
			setControlVal(value);
		}
	};

	const handleApply = () => {
		const payload = {
			selectedPars,
			validationCode,
			qualifierCode,
			controlVal,
		};
		applyValidationInfo(payload);
	};

	return (
		<div
			id="validation-popover"
			ref={ref}
			hidden={!pos || !visible}
			style={{
				top: getYPos(),
				left: getXPos(),
				width: POPOVER_WIDTH,
				height: POPOVER_HEIGHT,
			}}
		>
			<div id="validation-popover-left">
				<Label>Parameters</Label>
				<div id="validation-popover-parcode-container">
					{mapParCodesToList()}
				</div>
			</div>
			<div id="validation-popover-right">
				<div className="validation-popover-input-wrapper">
					<Label>Validation Code</Label>
					<select
						name={"validationCode"}
						value={validationCode}
						onChange={handleTextInput}
					>
						{mapValCodesToList()}
					</select>
				</div>
				<div className="validation-popover-input-wrapper">
					<Label>Qualifier Code(s)</Label>
					<input
						type="text"
						name="qualifierCode"
						value={qualifierCode}
						onChange={handleTextInput}
					/>
				</div>
				<div
					className="validation-popover-input-wrapper"
					hidden={!isAdjusted()}
				>
					<Label>Control Val</Label>
					<input
						type="text"
						name="controlVal"
						value={controlVal}
						onChange={handleTextInput}
					/>
				</div>
				<Button
					id="validation-popover-apply-button"
					onClick={handleApply}
				>
					Apply
				</Button>
			</div>
		</div>
	);
}

export default DataValidation;
